import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Shield as ShieldIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  ToggleOn as ToggleOnIcon,
} from '@mui/icons-material';
import { useNotification } from '../contexts/NotificationContext';
import { groupAPI, userAPI, tenantAPI, projectAPI, Group, Permission, User, Tenant, Project, FeatureOverride } from '../services/api';

interface GroupManagementProps {
  tenantId?: string;
}

const AVAILABLE_PERMISSIONS: Permission[] = [
  // Project permissions
  {
    id: 'project_create',
    name: 'Create Projects',
    description: 'Create new projects',
    resource_type: 'project',
    action: 'create',
  },
  {
    id: 'project_read',
    name: 'View Projects',
    description: 'View project details',
    resource_type: 'project',
    action: 'read',
  },
  {
    id: 'project_update',
    name: 'Update Projects',
    description: 'Modify project settings',
    resource_type: 'project',
    action: 'update',
  },
  {
    id: 'project_delete',
    name: 'Delete Projects',
    description: 'Delete projects',
    resource_type: 'project',
    action: 'delete',
  },
  // Dataset permissions
  {
    id: 'dataset_create',
    name: 'Upload Datasets',
    description: 'Upload new datasets',
    resource_type: 'dataset',
    action: 'create',
  },
  {
    id: 'dataset_read',
    name: 'View Datasets',
    description: 'View dataset details',
    resource_type: 'dataset',
    action: 'read',
  },
  {
    id: 'dataset_update',
    name: 'Update Datasets',
    description: 'Modify dataset metadata',
    resource_type: 'dataset',
    action: 'update',
  },
  {
    id: 'dataset_delete',
    name: 'Delete Datasets',
    description: 'Delete datasets',
    resource_type: 'dataset',
    action: 'delete',
  },
  // User management permissions
  {
    id: 'user_create',
    name: 'Create Users',
    description: 'Create new user accounts',
    resource_type: 'user',
    action: 'create',
  },
  {
    id: 'user_read',
    name: 'View Users',
    description: 'View user details',
    resource_type: 'user',
    action: 'read',
  },
  {
    id: 'user_update',
    name: 'Update Users',
    description: 'Modify user accounts',
    resource_type: 'user',
    action: 'update',
  },
  {
    id: 'user_delete',
    name: 'Delete Users',
    description: 'Delete user accounts',
    resource_type: 'user',
    action: 'delete',
  },
  // Group permissions
  {
    id: 'group_manage',
    name: 'Manage Groups',
    description: 'Create, update, and delete groups',
    resource_type: 'group',
    action: 'manage',
  },
  // Audit permissions
  {
    id: 'audit_read',
    name: 'View Audit Logs',
    description: 'View audit log entries',
    resource_type: 'audit',
    action: 'read',
  },
];

// Max users fetched per member search (backend caps /users at 500)
const MEMBER_CANDIDATE_PAGE_SIZE = 100;

export const GroupManagement: React.FC<GroupManagementProps> = ({ tenantId: propTenantId }) => {
  const { showNotification } = useNotification();
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Tenant state - fetch if not provided
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(propTenantId || '');

  // Effective tenant ID - from props or selected
  const tenantId = propTenantId || selectedTenantId;

  // Create/Edit form state
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  // Members state
  const [groupMembers, setGroupMembers] = useState<User[]>([]);
  // One server page of the tenant's users matching the member search
  const [candidatePage, setCandidatePage] = useState<User[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [candidateMatchCount, setCandidateMatchCount] = useState(0);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidateError, setCandidateError] = useState<string | null>(null);
  // Group whose members are loaded; candidates wait for it so current
  // members are never offered as addable
  const [membersLoadedFor, setMembersLoadedFor] = useState<string | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);
  // Group the members dialog was last opened for; member loads for any other
  // group are stale and dropped
  const openGroupIdRef = useRef<string | null>(null);

  // Feature overrides state
  const [showFeatureOverridesModal, setShowFeatureOverridesModal] = useState(false);
  const [featureOverrides, setFeatureOverrides] = useState<FeatureOverride[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [newFeaturePath, setNewFeaturePath] = useState('');
  const [newFeatureAllowed, setNewFeatureAllowed] = useState(true);

  // Common feature paths for autocomplete
  const COMMON_FEATURE_PATHS = [
    'modules.gus_chat.enabled',
    'modules.atlas.enabled',
    'modules.atlas.tabs.city_insights.enabled',
    'modules.atlas.tabs.city_satisfaction.enabled',
    'modules.atlas.tabs.trends.enabled',
    'modules.atlas.tabs.community.enabled',
    'modules.atlas.tabs.urban_pulse.enabled',
    'modules.atlas.tabs.urban_intelligence.enabled',
    'modules.atlas.tabs.spatial_economics.enabled',
    'modules.atlas.tabs.vulnerability.enabled',
    'modules.atlas.tabs.policy_intelligence.enabled',
    'modules.atlas.tabs.ai_assistant.enabled',
    'modules.reports.enabled',
    'modules.map.enabled',
    'modules.assessments.enabled',
    'modules.data_management.enabled',
    'modules.dashboard.enabled',
    'modules.maps_library.enabled',
  ];

  // Load tenants if not provided via props
  useEffect(() => {
    if (!propTenantId) {
      loadTenants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propTenantId]);

  const loadTenants = async () => {
    try {
      const tenantList = await tenantAPI.list();
      setTenants(tenantList || []);
      // Select the first tenant by default
      if (tenantList && tenantList.length > 0 && !selectedTenantId) {
        setSelectedTenantId(tenantList[0].id);
      }
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  useEffect(() => {
    if (tenantId) {
      loadGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const response = await groupAPI.list(tenantId);
      setGroups(response.groups || []);
    } catch (error: any) {
      console.error('Load groups error:', error);
      showNotification('Failed to load groups', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setGroupName('');
    setGroupDescription('');
    setSelectedPermissions([]);
    setShowCreateModal(true);
  };

  const openEditModal = (group: Group) => {
    setSelectedGroup(group);
    setGroupName(group.name);
    setGroupDescription(group.description || '');
    setSelectedPermissions(group.permissions?.map((p) => p.id) || []);
    setShowEditModal(true);
  };

  const openMembersModal = async (group: Group) => {
    openGroupIdRef.current = group.id;
    setSelectedGroup(group);
    setGroupMembers([]);
    setCandidatePage([]);
    setCandidateMatchCount(0);
    setCandidateError(null);
    setMemberSearch('');
    setMembersLoadedFor(null);
    setMembersError(null);
    setShowMembersModal(true);
    await refreshMembers(group);
  };

  const refreshMembers = async (group: Group) => {
    setLoading(true);
    let stale = false;

    try {
      // Load group members
      const membersResponse = await groupAPI.getMembers(group.id);
      stale = openGroupIdRef.current !== group.id;
      if (stale) return;
      setGroupMembers(membersResponse.members || []);
      setMembersLoadedFor(group.id);
      setMembersError(null);
    } catch (error: any) {
      stale = openGroupIdRef.current !== group.id;
      if (stale) return;
      console.error('Load members error:', error);
      setMembersError(error.response?.data?.detail || 'Failed to load group members');
      showNotification('Failed to load group members', 'error');
    } finally {
      if (!stale) setLoading(false);
    }
  };

  // Search the tenant's users to offer as new members. /users is paginated,
  // so the search runs server-side: one page filtered client-side hides
  // everyone past it.
  useEffect(() => {
    if (!showMembersModal || !selectedGroup || membersLoadedFor !== selectedGroup.id) return;

    let cancelled = false;
    setLoadingCandidates(true);
    const timer = setTimeout(async () => {
      try {
        const usersResponse = await userAPI.list({
          tenant_id: selectedGroup.tenant_id,
          search: memberSearch.trim() || undefined,
          limit: MEMBER_CANDIDATE_PAGE_SIZE,
        });
        if (cancelled) return;
        const page = usersResponse.users || [];
        setCandidatePage(page);
        setCandidateMatchCount(usersResponse.total ?? page.length);
        setCandidateError(null);
      } catch (error: any) {
        if (cancelled) return;
        console.error('Load users error:', error);
        setCandidatePage([]);
        setCandidateMatchCount(0);
        setCandidateError(error.response?.data?.detail || 'Could not load users');
      } finally {
        if (!cancelled) setLoadingCandidates(false);
      }
    }, memberSearch ? 300 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showMembersModal, selectedGroup, membersLoadedFor, memberSearch]);

  // Current members are filtered out here, not on the server, so
  // `candidateMatchCount` still counts them
  const availableUsers = useMemo(() => {
    const memberIds = new Set(groupMembers.map((m) => m.id));
    return candidatePage.filter((u) => !memberIds.has(u.id));
  }, [candidatePage, groupMembers]);

  const candidatePageTruncated = candidateMatchCount > candidatePage.length;

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      showNotification('Group name is required', 'error');
      return;
    }

    if (selectedPermissions.length === 0) {
      showNotification('At least one permission must be selected', 'error');
      return;
    }

    setLoading(true);
    try {
      await groupAPI.create({
        name: groupName.trim(),
        description: groupDescription.trim() || undefined,
        tenant_id: tenantId,
      });

      showNotification(`Group "${groupName}" created successfully`, 'success');
      setShowCreateModal(false);
      loadGroups();
    } catch (error: any) {
      console.error('Create group error:', error);
      // Handle validation errors that return an object/array instead of string
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to create group';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors are arrays of {loc, msg, type}
        errorMessage = detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!selectedGroup || !groupName.trim()) {
      showNotification('Group name is required', 'error');
      return;
    }

    if (selectedPermissions.length === 0) {
      showNotification('At least one permission must be selected', 'error');
      return;
    }

    setLoading(true);
    try {
      // TODO: Replace with actual API call
      // await groupAPI.update(selectedGroup.id, {
      //   name: groupName,
      //   description: groupDescription,
      //   permission_ids: selectedPermissions,
      // });

      showNotification(`Group "${groupName}" updated successfully`, 'success');
      setShowEditModal(false);
      loadGroups();
    } catch (error: any) {
      console.error('Update group error:', error);
      showNotification('Failed to update group', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (group: Group) => {
    if (group.is_system) {
      showNotification('Cannot delete system groups', 'error');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete group "${group.name}"?`)) {
      return;
    }

    setLoading(true);
    try {
      // TODO: Replace with actual API call
      // await groupAPI.delete(group.id);

      showNotification(`Group "${group.name}" deleted successfully`, 'success');
      loadGroups();
    } catch (error: any) {
      console.error('Delete group error:', error);
      showNotification('Failed to delete group', 'error');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permissionId: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const addUserToGroup = async (userId: string) => {
    if (!selectedGroup) return;
    const groupId = selectedGroup.id;

    setLoading(true);
    try {
      await groupAPI.addMember({
        group_id: selectedGroup.id,
        user_id: userId,
      });

      // Move them across now so the Add button can't be pressed twice
      // before the reload lands
      const added = candidatePage.find((u) => u.id === userId);
      if (added && openGroupIdRef.current === groupId) setGroupMembers((prev) => [...prev, added]);
      showNotification('User added to group', 'success');
      // Reload members to refresh the lists
      await refreshMembers(selectedGroup);
    } catch (error: any) {
      console.error('Add member error:', error);
      showNotification(error.response?.data?.detail || 'Failed to add user to group', 'error');
    } finally {
      setLoading(false);
    }
  };

  const removeUserFromGroup = async (userId: string) => {
    if (!selectedGroup) return;
    const groupId = selectedGroup.id;

    setLoading(true);
    try {
      await groupAPI.removeMember(selectedGroup.id, userId);

      if (openGroupIdRef.current === groupId) {
        setGroupMembers((prev) => prev.filter((m) => m.id !== userId));
      }
      showNotification('User removed from group', 'success');
      // Reload members to refresh the lists
      await refreshMembers(selectedGroup);
    } catch (error: any) {
      console.error('Remove member error:', error);
      showNotification(error.response?.data?.detail || 'Failed to remove user from group', 'error');
    } finally {
      setLoading(false);
    }
  };

  const groupPermissionsByResource = () => {
    const grouped: Record<string, Permission[]> = {};
    AVAILABLE_PERMISSIONS.forEach((perm) => {
      if (!grouped[perm.resource_type]) {
        grouped[perm.resource_type] = [];
      }
      grouped[perm.resource_type].push(perm);
    });
    return grouped;
  };

  // Feature override handlers
  const openFeatureOverridesModal = async (group: Group) => {
    setSelectedGroup(group);
    setShowFeatureOverridesModal(true);
    setLoading(true);

    try {
      // Load projects for this tenant
      const projectsResponse = await projectAPI.list(group.tenant_id);
      setProjects(projectsResponse.projects || []);

      // Load existing overrides
      const overridesResponse = await groupAPI.listFeatureOverrides(group.id);
      setFeatureOverrides(overridesResponse.overrides || []);
    } catch (error: any) {
      console.error('Load feature overrides error:', error);
      showNotification('Failed to load feature overrides', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeatureOverride = async () => {
    if (!selectedGroup || !selectedProjectId || !newFeaturePath) {
      showNotification('Please select a project and enter a feature path', 'error');
      return;
    }

    setLoading(true);
    try {
      await groupAPI.createFeatureOverride(selectedGroup.id, {
        project_id: selectedProjectId,
        feature_path: newFeaturePath,
        is_allowed: newFeatureAllowed,
      });

      showNotification('Feature override added successfully', 'success');

      // Reload overrides
      const overridesResponse = await groupAPI.listFeatureOverrides(selectedGroup.id);
      setFeatureOverrides(overridesResponse.overrides || []);

      // Reset form
      setNewFeaturePath('');
      setNewFeatureAllowed(true);
    } catch (error: any) {
      console.error('Add feature override error:', error);
      showNotification(error.response?.data?.detail || 'Failed to add feature override', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeatureOverride = async (override: FeatureOverride) => {
    if (!selectedGroup) return;

    setLoading(true);
    try {
      await groupAPI.updateFeatureOverride(selectedGroup.id, override.id, !override.is_allowed);

      showNotification('Feature override updated', 'success');

      // Reload overrides
      const overridesResponse = await groupAPI.listFeatureOverrides(selectedGroup.id);
      setFeatureOverrides(overridesResponse.overrides || []);
    } catch (error: any) {
      console.error('Update feature override error:', error);
      showNotification(error.response?.data?.detail || 'Failed to update feature override', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFeatureOverride = async (overrideId: string) => {
    if (!selectedGroup) return;

    if (!window.confirm('Are you sure you want to remove this feature override?')) {
      return;
    }

    setLoading(true);
    try {
      await groupAPI.deleteFeatureOverride(selectedGroup.id, overrideId);

      showNotification('Feature override removed', 'success');

      // Reload overrides
      const overridesResponse = await groupAPI.listFeatureOverrides(selectedGroup.id);
      setFeatureOverrides(overridesResponse.overrides || []);
    } catch (error: any) {
      console.error('Delete feature override error:', error);
      showNotification(error.response?.data?.detail || 'Failed to remove feature override', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Manage user groups and access control
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadGroups}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateModal} disabled={loading}>
            Create Group
          </Button>
        </Box>
      </Box>

      {/* Tenant Selector (when not provided via props) */}
      {!propTenantId && tenants.length > 0 && (
        <FormControl sx={{ mb: 3, minWidth: 300 }}>
          <InputLabel>Organization</InputLabel>
          <Select
            value={selectedTenantId}
            label="Organization"
            onChange={(e) => setSelectedTenantId(e.target.value)}
          >
            {tenants.map((tenant) => (
              <MenuItem key={tenant.id} value={tenant.id}>
                {tenant.organization_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {loading && groups.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {groups.map((group) => (
            <Grid item xs={12} sm={6} md={4} key={group.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: group.is_system ? 2 : 1,
                  borderColor: group.is_system ? 'primary.main' : 'divider',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GroupIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                      {group.name}
                    </Typography>
                    {group.is_system && (
                      <Chip label="System" color="primary" size="small" />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {group.description || 'No description'}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" color="primary.main">
                          {group.member_count || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Members
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" color="secondary.main">
                          {group.permissions?.length || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Permissions
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<PeopleIcon />}
                      onClick={() => openMembersModal(group)}
                    >
                      Members
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ToggleOnIcon />}
                      onClick={() => openFeatureOverridesModal(group)}
                      color="secondary"
                    >
                      Features
                    </Button>
                  </Box>
                  <Box>
                    {!group.is_system && (
                      <>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEditModal(group)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteGroup(group)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Group Modal */}
      <Dialog
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Group</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g., Data Scientists"
              disabled={loading}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              placeholder="Describe the purpose of this group..."
              multiline
              rows={3}
              disabled={loading}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ShieldIcon sx={{ mr: 1 }} /> Permissions
            </Typography>

            <Box>
              {Object.entries(groupPermissionsByResource()).map(([resource, perms]) => (
                <Box key={resource} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {resource.charAt(0).toUpperCase() + resource.slice(1)} Permissions
                  </Typography>
                  <Grid container spacing={1}>
                    {perms.map((perm) => (
                      <Grid item xs={12} sm={6} key={perm.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(perm.id)}
                              onChange={() => togglePermission(perm.id)}
                              disabled={loading}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {perm.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {perm.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateModal(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Modal */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Group: {selectedGroup?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              disabled={loading || selectedGroup?.is_system}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              multiline
              rows={3}
              disabled={loading}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ShieldIcon sx={{ mr: 1 }} /> Permissions
            </Typography>

            <Box>
              {Object.entries(groupPermissionsByResource()).map(([resource, perms]) => (
                <Box key={resource} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {resource.charAt(0).toUpperCase() + resource.slice(1)} Permissions
                  </Typography>
                  <Grid container spacing={1}>
                    {perms.map((perm) => (
                      <Grid item xs={12} sm={6} key={perm.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(perm.id)}
                              onChange={() => togglePermission(perm.id)}
                              disabled={loading}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {perm.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {perm.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateGroup}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Update Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Modal */}
      <Dialog
        open={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage Members: {selectedGroup?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Members ({groupMembers.length})
            </Typography>
            {membersError && (
              <Alert
                severity="error"
                sx={{ mb: 1 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => selectedGroup && refreshMembers(selectedGroup)}
                    disabled={loading}
                  >
                    Retry
                  </Button>
                }
              >
                Could not load members: {membersError}
              </Alert>
            )}
            {membersLoadedFor !== selectedGroup?.id ? (
              !membersError && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )
            ) : groupMembers.length === 0 ? (
              <Alert severity="info">No members in this group</Alert>
            ) : (
              <List>
                {groupMembers.map((user) => (
                  <ListItem key={user.id} divider sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                      <ListItemText
                        primary={user.full_name || user.username || user.email}
                        secondary={user.email}
                        sx={{ flex: 1 }}
                      />
                      <Button
                        size="small"
                        color="error"
                        onClick={() => removeUserFromGroup(user.id)}
                        disabled={loading}
                      >
                        Remove
                      </Button>
                    </Box>
                    {/* Show project access/roles */}
                    {user.project_access && user.project_access.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.project_access.map((access: any) => (
                          <Chip
                            key={access.project_id}
                            label={`${access.project_name}: ${access.role}${access.is_default ? ' ★' : ''}`}
                            size="small"
                            variant="outlined"
                            color={access.role === 'admin' ? 'primary' : 'default'}
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </Box>
                    )}
                    {(!user.project_access || user.project_access.length === 0) && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        No project access assigned
                      </Typography>
                    )}
                  </ListItem>
                ))}
              </List>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom>
              Add Members
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email..."
              inputProps={{ 'aria-label': 'Search users to add' }}
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              disabled={membersLoadedFor !== selectedGroup?.id}
              error={Boolean(candidateError)}
              helperText={
                membersLoadedFor !== selectedGroup?.id
                  ? 'User search is available once the current members load.'
                  : loadingCandidates
                    ? 'Searching...'
                    : candidateError
                      ? `Could not search users: ${candidateError}. Edit the search to try again.`
                      : candidatePageTruncated && availableUsers.length > 0
                        ? memberSearch.trim()
                          ? `Only the first ${candidatePage.length} of ${candidateMatchCount} matches were searched. Keep typing to narrow.`
                          : `${candidateMatchCount} users in this organization. Type a name or email to search them all.`
                        : undefined
              }
              sx={{ mb: 1 }}
            />
            {membersLoadedFor !== selectedGroup?.id || candidateError ? null : loadingCandidates && availableUsers.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : availableUsers.length === 0 ? (
              <Alert severity="info">
                {candidatePageTruncated
                  ? `The first ${candidatePage.length} matches are already members. Type a name or email to find others.`
                  : memberSearch.trim() ? 'No matching users outside this group' : 'No available users to add'}
              </Alert>
            ) : (
              <List>
                {availableUsers.map((user) => (
                  <ListItem key={user.id} divider sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                      <ListItemText
                        primary={user.full_name || user.username || user.email}
                        secondary={user.email}
                        sx={{ flex: 1 }}
                      />
                      <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => addUserToGroup(user.id)}
                        aria-label={`Add ${user.email}`}
                        disabled={loading}
                      >
                        Add
                      </Button>
                    </Box>
                    {/* Show user's role */}
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={`Role: ${user.role}`}
                        size="small"
                        variant="outlined"
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        sx={{ fontSize: '0.75rem' }}
                      />
                      {!user.is_active && (
                        <Chip
                          label="Inactive"
                          size="small"
                          color="warning"
                          sx={{ ml: 0.5, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMembersModal(false)} disabled={loading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feature Overrides Modal */}
      <Dialog
        open={showFeatureOverridesModal}
        onClose={() => setShowFeatureOverridesModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleOnIcon color="secondary" />
            Feature Overrides: {selectedGroup?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Feature overrides allow you to enable or disable specific features for members of this group.
              When a user belongs to multiple groups, the most restrictive setting applies.
            </Alert>

            {/* Add new override form */}
            <Typography variant="subtitle2" gutterBottom>
              Add Feature Override
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Project</InputLabel>
                <Select
                  value={selectedProjectId}
                  label="Project"
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  disabled={loading}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                freeSolo
                options={COMMON_FEATURE_PATHS}
                value={newFeaturePath}
                onChange={(_, value) => setNewFeaturePath(value || '')}
                onInputChange={(_, value) => setNewFeaturePath(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Feature Path"
                    placeholder="modules.atlas.enabled"
                    sx={{ minWidth: 300 }}
                    disabled={loading}
                  />
                )}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={newFeatureAllowed}
                    onChange={(e) => setNewFeatureAllowed(e.target.checked)}
                    disabled={loading}
                  />
                }
                label={newFeatureAllowed ? 'Allowed' : 'Denied'}
              />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddFeatureOverride}
                disabled={loading || !selectedProjectId || !newFeaturePath}
              >
                Add Override
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Existing overrides table */}
            <Typography variant="subtitle2" gutterBottom>
              Current Overrides ({featureOverrides.length})
            </Typography>
            {featureOverrides.length === 0 ? (
              <Alert severity="info">No feature overrides configured for this group</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Project</TableCell>
                      <TableCell>Feature Path</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {featureOverrides.map((override) => (
                      <TableRow key={override.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {override.project_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {override.project_slug}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                          >
                            {override.feature_path}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={override.is_allowed ? 'Allowed' : 'Denied'}
                            color={override.is_allowed ? 'success' : 'error'}
                            size="small"
                            onClick={() => handleToggleFeatureOverride(override)}
                            sx={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Toggle status">
                            <IconButton
                              size="small"
                              onClick={() => handleToggleFeatureOverride(override)}
                              disabled={loading}
                            >
                              <ToggleOnIcon color={override.is_allowed ? 'success' : 'error'} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove override">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteFeatureOverride(override.id)}
                              disabled={loading}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFeatureOverridesModal(false)} disabled={loading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupManagement;
